/**
 * Kontrastberechnung nach WCAG 2.1.
 *
 * Warum zur Laufzeit und nicht als Tabelle in der Dokumentation: die Marke
 * haengt an drei Seeds in brand.css. Wer die austauscht, verschiebt jede
 * abgeleitete Farbe — und eine handgepflegte Kontrasttabelle waere ab diesem
 * Moment falsch, ohne dass es jemandem auffiele. Gerechnet wird deshalb an dem,
 * was der Browser tatsaechlich darstellt.
 */

/** Loest einen CSS-Farbwert (auch var(...)) in ein RGB-Tripel auf. */
export function resolveColor(cssValue: string): [number, number, number] | null {
  const probe = document.createElement('span')
  probe.style.display = 'none'
  probe.style.color = cssValue
  document.body.appendChild(probe)

  // Der Browser normalisiert auf rgb()/rgba() — das ist der Trick, mit dem
  // hsl(), color-mix() und Verschachtelungen von var() ohne eigenen Parser
  // auskommen.
  const computed = getComputedStyle(probe).color
  document.body.removeChild(probe)

  const match = computed.match(/-?[\d.]+/g)
  if (!match || match.length < 3) return null

  return [Number(match[0]), Number(match[1]), Number(match[2])]
}

/** Relative Leuchtdichte nach WCAG. */
function luminance([r, g, b]: [number, number, number]): number {
  const channel = (value: number) => {
    const c = value / 255
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/** Kontrastverhaeltnis zwischen 1 und 21. */
export function contrastRatio(foreground: string, background: string): number | null {
  const fg = resolveColor(foreground)
  const bg = resolveColor(background)
  if (!fg || !bg) return null

  const lighter = Math.max(luminance(fg), luminance(bg))
  const darker = Math.min(luminance(fg), luminance(bg))
  return (lighter + 0.05) / (darker + 0.05)
}

export type ContrastGrade = 'AAA' | 'AA' | 'AA-large' | 'fail'

/**
 * Bewertung. `large` gilt ab 24px bzw. ab 18.66px fett — dort reicht 3:1.
 * Nicht-textliche Elemente (Rahmen, Icons) brauchen ebenfalls 3:1.
 */
export function gradeContrast(ratio: number, large = false): ContrastGrade {
  if (large) {
    if (ratio >= 4.5) return 'AAA'
    if (ratio >= 3) return 'AA-large'
    return 'fail'
  }
  if (ratio >= 7) return 'AAA'
  if (ratio >= 4.5) return 'AA'
  if (ratio >= 3) return 'AA-large'
  return 'fail'
}
